import * as $ from "jquery";

/**
 * Enables a dropdown menu to open on focus and close once focus leaves.
 */
const init = (containerSelector: string, activeClass: string, excludeState: string) => {
	const setUpTriggers = () => {
		const $containers = $(containerSelector + ':not(.accessible-dropdown-set-up)');
		if ($containers.length === 0) {
			return;
		}
		$containers.each((index, element) => {
			const $container = $(element);
			$container.addClass('accessible-dropdown-set-up');

			$container.find(':input,select,a,span:not(.do-not-attach-tabindex)').each((index: number, elem: HTMLElement) => {
				elem.tabIndex = -1
			});

			const dropFocus = (event: Event) => {
				if (!$container.is(event.target as HTMLElement) && $container.has(event.target as HTMLElement).length === 0) {
					close();
				}
			}

			const open = () => {
				$container.find(':input,select,a,span:not(.do-not-attach-tabindex)').each((index: number, elem: HTMLElement) => {
					elem.tabIndex = 0
				});
				$container.addClass(activeClass);
				$(document).on('mouseup.containerFocusout focusin.containerFocusout', dropFocus);
			}

			const close = () => {
				$container.find(':input,select,a,span:not(.do-not-attach-tabindex)').each((index: number, elem: HTMLElement) => {
					elem.tabIndex = 0
				});
				$container.removeClass(activeClass);
				$container.off('.containerFocusout');
			}

			const isOpen = () => {
				return $container.hasClass(activeClass);
			}

			const handleToggle = (event: JQuery.Event) => {
				event.preventDefault();
				if (isOpen()) {
					close();
				} else {
					open();
				}
			}

			$container.on('click', '.accessibleDropdown--opener', (event) => {
				handleToggle(event);
			});

			// Open on space bar or enter
			$container.on('keypress', (event) => {
				if (event.target !== $container[0]) {
					return true;
				}
				if (event.key === ' ' || event.key === 'Enter') {
					event.preventDefault();
					handleToggle(event);
					return false;
				}
			});

			// Open if an input inside 'more fields' receives focus
			$container.children().on('focusin', (event) => {
				if (!$container.hasClass(activeClass)) {
					$(document).on('mouseup.containerFocusout focusin.containerFocusout', dropFocus);
				}
				open();
			});

			$container.on('click', 'input[name="update"]', close);

			// When surrounding form is submitted close 'more filters'
			$container.closest('form').on('submit', close);

			if ($container.hasClass('usesRangeLabel')) {
				const calcRangeLabel = () => {
					const minValue = String($container.find('.rangeLabelMin').val() || '').trim();
					const maxValue = String($container.find('.rangeLabelMax').val() || '').trim();
					const labelElementPrefix = $container.data('label-element-prefix') ?? '';
					const labelElementSeparator = $container.data('label-element-separator') ?? ' - ';
					const labelDefault = $container.data('label-default');
					let str = '';
					if (minValue === '') {
						if (maxValue === '') {
							str = labelDefault;
						} else {
							str = labelDefault + labelElementSeparator + labelElementPrefix + maxValue;
						}
					} else {
						if (maxValue === '') {
							str = labelElementPrefix + minValue + labelElementSeparator + labelDefault;
						} else {
							str = labelElementPrefix + minValue + labelElementSeparator + labelElementPrefix + maxValue;
						}
					}
					$container.find('.rangeLabel').each((idx, ele) => {ele.innerHTML = str});
				};
				$container.on('change', '.rangeLabelMin, .rangeLabelMax', event => {
					calcRangeLabel();
				});
				calcRangeLabel();
			}

			if ($container.hasClass('usesCountLabel')) {
				const calcCountLabel = () => {
					let count = 0;
					$container.find("[data-default-value]").each((idx, ele) => {
						const defaultValue = ele.getAttribute("data-default-value");
						if (ele.tagName === "DIV") {
							const inputs = ele.querySelectorAll("input");
							if (defaultValue === "") {
								for (let i = 0; i < inputs.length; i++) {
									if (inputs.item(i).checked === true) {
										count = count + 1;
									}
								}
							} else {
								for (let i = 0; i < inputs.length; i++) {
									if (inputs.item(i).checked === true) {
										if (defaultValue.indexOf(inputs.item(i).value) === -1) {
											count = count + 1;
										}
									} else if (defaultValue.indexOf(inputs.item(i).value) !== -1) {
										count = count + 1;
									}
								}
							}
						} else if (ele.tagName === "INPUT") {
							if ((ele as HTMLInputElement).type === "checkbox" || (ele as HTMLInputElement).type === "radio") {
								if ((ele as HTMLInputElement).checked !== (defaultValue === "t")) {
									count = count + 1;
								}
							} else if ((ele as HTMLInputElement).value !== defaultValue) {
								count = count + 1;
							}
						} else if (ele.tagName === "SELECT") {
							if ((ele as HTMLSelectElement).value !== defaultValue) {
								count = count + 1;
							}
						}
						const labelElementPrefix = $container.data('label-element-prefix') ?? '';
						const labelDefault = $container.data('label-default');
						$container.find('.countLabel').each((idx, ele) => {
							ele.innerHTML = count === 0 ? labelDefault : labelElementPrefix + '<span class="do-not-attach-tabindex count">(' + count + ")</span>";
						});
					});
				};
				$container.on('change', 'input,select', event => {
					calcCountLabel();
				});
				calcCountLabel();
			}

			if ($container.hasClass('usesCheckboxLabel')) {
				const calcCheckboxLabel = () => {
					const selectedOptions: string[] = [];
					$container.find('input:checked').each((idx, ele) => {
						selectedOptions.push(ele.getAttribute("label"));
					});
					let str = "";
					if (selectedOptions.length === $container.find('input[type="checkbox"]').length) {
						str = ", All";
					} else {
						for (let i = 0; i < selectedOptions.length; i++) {
							str = str + ", " + selectedOptions[i];
						}
					}
					if (str.length <= 2) {
						$container.find('.checkboxLabel div').each((idx, ele) => {
							const originalText = ele.getAttribute("data-original-text");
							if (originalText !== null && originalText !== undefined) {
								ele.innerHTML = originalText;
								ele.removeAttribute("data-original-text");
							}
						});
					} else {
						str = str.substring(2);
						$container.find('.checkboxLabel div').each((idx, ele) => {
							const originalText = ele.getAttribute("data-original-text");
							if (originalText === null || originalText === undefined) {
								ele.setAttribute("data-original-text", ele.innerHTML);
							}
							ele.innerHTML = str
						});
					}
				};
				$container.on('change', 'input[type="checkbox"]', event => {
					calcCheckboxLabel();
				});
				calcCheckboxLabel();
			}
		});
	};
	setUpTriggers();
	document.addEventListener('jsfAjaxEvent', event => {
		if ((event as any).detail.fireType === 'always') {
			setUpTriggers();
			const allDropdowns = document.querySelectorAll(".accessibleDropdown.accessible-dropdown-set-up");
			for (let i = 0; i < allDropdowns.length; i++) {
				const dropdown = allDropdowns.item(i);
				if (dropdown.querySelectorAll(".containsMessages").length !== 0) {
					dropdown.classList.add("containsMessages");
				} else {
					dropdown.classList.remove("containsMessages");
				}
			}
		}
	});
}

export default init;
